/**
 * A document's segments as the viewer lists them. The server sends the segment tree with
 * offsets in Unicode code points (`SegmentBody`); JavaScript strings count UTF-16 units, so
 * text is cut by code points here, never with `String.slice` on the offsets directly.
 */

/** The server's segment tree, as decoded from `SegmentBody`. */
export interface SegmentNode {
  readonly segmentType: string;
  /** Empty for the document itself; otherwise steps such as `subentry.2/sentence.1`. */
  readonly path: string;
  /** Code points, start inclusive, end exclusive. */
  readonly start: number;
  readonly end: number;
  readonly children: readonly SegmentNode[];
}

export interface DocumentSegment {
  /** The segment path of the server's segment reference, for example `subentry.2`. */
  readonly segmentPath: string;
  readonly segmentType: string;
  readonly text: string;
  /** Code points in the document text, start inclusive, end exclusive: where it is on a page map. */
  readonly start: number;
  readonly end: number;
}

/**
 * The smallest segments of the document, in text order, each with its text. They cover
 * what the segmentation splits without repeating text. A document without split rules
 * has none: it is one segment, shown as its full text.
 */
export function listLeafSegments(text: string, root: SegmentNode): DocumentSegment[] {
  const codePoints = Array.from(text);
  const leaves: DocumentSegment[] = [];
  const collect = (node: SegmentNode) => {
    if (node.children.length === 0) {
      if (node.path !== "") {
        leaves.push({
          segmentPath: node.path,
          segmentType: node.segmentType,
          text: codePoints.slice(node.start, node.end).join(""),
          start: node.start,
          end: node.end,
        });
      }
      return;
    }
    node.children.forEach(collect);
  };
  collect(root);
  return leaves;
}
